// ─────────────────────────────────────────────────────────────────────────────
// Algolia autocomplete hooks — CB Shopify theme
//
// Loaded on every Algolia-enabled page (the header search bar is global).
// Handles: predictive search product card template + debounce/QS plugin options.
//
// Confirmed: all metafields are indexed under meta.custom.<field_name>
// ─────────────────────────────────────────────────────────────────────────────

function debouncePromise(fn, time) {
  let timer
  return function debounced(...args) {
    if (timer) clearTimeout(timer)
    return new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        try {
          resolve(fn(...args))
        } catch (err) {
          reject(err)
        }
      }, time)
    })
  }
}

document.addEventListener('algolia.hooks.initialize', function () {
  // Read inside the listener so defer order can never race against the inline config script.
  const debounceMs = window.algoliaDebounceMs ?? 200
  // ── US-2: Autocomplete product card (predictive search dropdown) ───────────
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTemplate',
    (
      _defaultTemplate,
      templateOptions,
      distinct,
      itemLink,
      trackSearchAttribution
    ) => {
      const { html, item, components } = templateOptions
      const image = algoliaShopify.helpers.renderImage(item, 120)
      // Algolia indexes one record per VARIANT; with distinct=true the surfaced
      // record is the most *relevant* variant, not the cheapest, so item.price
      // overstates the price whenever a plus-size variant wins (CB-40).
      // variants_min_price / variants_compare_at_price_min are product-level
      // aggregates Algolia precomputes for distinct mode — always use them as
      // a matched pair (never mix with the per-variant fields) so the
      // strikethrough and "From" price always describe the same variant.
      const minPrice = parseFloat(item.variants_min_price)
      const minCompareAt = parseFloat(item.variants_compare_at_price_min)
      // Guard on > 0 (not ??) so a $0 aggregate (possible when an unpriced
      // hidden variant drags the index min down — custom.hidden_from_site is
      // invisible to Algolia) degrades to the surfaced-variant price rather
      // than blanking the price entirely.
      const useAggregates = minPrice > 0
      const basePrice = useAggregates ? minPrice : parseFloat(item.price)
      const compareAt = useAggregates
        ? minCompareAt
        : parseFloat(item.compare_at_price)
      const hasCompareAt = compareAt > 0 && compareAt > basePrice
      const priceVisible = basePrice > 0
      // Defensive: window.CBAlgolia is loaded on all pages since the CB-39 fix, but
      // fall back to the Algolia-provided helper if the namespace isn't available so
      // the custom aa-product-card template still renders rather than throwing and
      // letting the app silently swap in its own default markup.
      const displayedPrice = priceVisible
        ? window.CBAlgolia?.fromPrice
          ? window.CBAlgolia.fromPrice(Math.round(basePrice * 100))
          : algoliaShopify.helpers.displayPrice(item, distinct)
        : ''

      return html`
        <a
          href="${itemLink}"
          class="aa-product-card"
          onClick="${() => trackSearchAttribution(item)}"
        >
          <div class="aa-product-card__image">
            <img
              src="${image.src}"
              srcset="${image.srcset}"
              sizes="48px"
              alt="${item.title}"
              loading="lazy"
            />
          </div>
          <div class="aa-product-card__body">
            <span class="aa-product-card__title">
              ${components.Highlight({ hit: item, attribute: 'title' })}
            </span>
            ${priceVisible
              ? html`<span class="aa-product-card__price">
                  ${hasCompareAt
                    ? html`<s class="aa-product-card__price-compare"
                          >${algoliaShopify.helpers.displayStrikedPrice(
                            basePrice,
                            compareAt
                          )}</s
                        >
                        <span class="aa-product-card__price-sale"
                          >${displayedPrice}</span
                        >`
                    : html`${displayedPrice}`}
                </span>`
              : ''}
          </div>
        </a>
      `
    }
  )

  // ── Stop clicks inside aa-Panel from bubbling to document listeners that
  //    might close the overlay (e.g. the offcanvas backdrop click handler).
  //    Uses a MutationObserver so it works regardless of when Algolia mounts
  //    the panel, and guards with data-algolia-init against double-binding.
  const aaPanelClickObserver = new MutationObserver(() => {
    document
      .querySelectorAll('.aa-Panel:not([data-algolia-init])')
      .forEach((panel) => {
        panel.setAttribute('data-algolia-init', '')
        panel.addEventListener('click', (e) => e.stopPropagation())
      })
  })
  aaPanelClickObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // ── Plugin array: add QS plugin + debounce all plugin sources ────────────
  // beforeAutocompletePluginsArray is the correct hook for adding/removing
  // plugins (beforeAutocompleteOptions only handles general options).
  algoliaShopify.hooks.registerHook(
    'beforeAutocompletePluginsArray',
    (...plugins) => {
      if (
        window.algoliaQsIndexName &&
        window['@algolia/autocomplete-plugin-query-suggestions']
      ) {
        plugins.push(
          window[
            '@algolia/autocomplete-plugin-query-suggestions'
          ].createQuerySuggestionsPlugin({
            searchClient: algoliaShopify.searchClient,
            indexName: window.algoliaQsIndexName,
          })
        )
      }

      return plugins.map((plugin) => {
        if (!plugin || typeof plugin.getSources !== 'function') return plugin
        return {
          ...plugin,
          getSources: debouncePromise(
            (...args) => Promise.resolve(plugin.getSources(...args)),
            debounceMs
          ),
        }
      })
    }
  )

  // placeholder mirrors the native input's locale string so both paths
  // display identically regardless of which is active.
  algoliaShopify.hooks.registerHook('beforeAutocompleteOptions', (options) => ({
    ...options,
    placeholder:
      window.algoliaSettings?.searchPlaceholder ?? 'Search our store',
    stallThreshold: debounceMs + 300,
  }))
})
